import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import pdfMake from "../pdfMakeSetup";
import "./GlobalClosure.css";

function GlobalClosure() {
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filteredClosures, setFilteredClosures] = useState([]);
  const [searchDate, setSearchDate] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: "",
    end: ""
  });
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [billsForSelectedClosure, setBillsForSelectedClosure] = useState([]);
  const [allBills, setAllBills] = useState([]);

  const authState = useSelector(state => state.AuthReducer);
  const user = authState?.user;

  // Fonction pour calculer le total TTC d'une facture (comme dans BillsFilter)
  const calculateBillTotalTTC = (bill) => {
    if (!bill) return 0;
    
    // 1. Si la facture a déjà totalTTC (c'est le champ principal)
    if (bill.totalTTC !== undefined && bill.totalTTC !== null) {
      return bill.totalTTC;
    }
    // 2. Si la facture a total et tva séparés
    else if (bill.total !== undefined && bill.tva !== undefined) {
      return bill.total + (bill.tva || 0);
    }
    // 3. Si la facture a totalHT et tva
    else if (bill.totalHT !== undefined && bill.tva !== undefined) {
      return bill.totalHT + bill.tva;
    }
    // 4. Si on a seulement total (on suppose TTC)
    else if (bill.total !== undefined) {
      return bill.total;
    }
    // 5. Calculer depuis les items
    else if (bill.items && bill.items.length > 0) {
      return bill.items.reduce((sum, item) => {
        // Le prix des items est déjà en TTC dans la base
        const itemPrice = item.price || 0;
        const quantity = item.quantity || 0;
        return sum + (itemPrice * quantity);
      }, 0);
    }
    // 6. Par défaut
    else {
      return 0;
    }
  };

  // Fonction pour calculer le total HT d'une facture
  const calculateBillTotalHT = (bill) => {
    const totalTTC = calculateBillTotalTTC(bill);
    // TVA 7%
    return totalTTC / 1.07;
  };

  // Fonction pour calculer les stats de paiement
  const calculatePaymentStatsFromBills = (bills) => {
    const paymentStats = {};
    
    bills.forEach(bill => {
      const method = bill.paymentMethod || "Non spécifié";
      const billTotalTTC = calculateBillTotalTTC(bill);
      
      paymentStats[method] = (paymentStats[method] || 0) + billTotalTTC;
    });
    
    return paymentStats;
  };

  useEffect(() => {
    fetchAllClosures();
    fetchAllLocations();
    fetchAllBills(); // Charger toutes les factures une fois
  }, []);

  // Charger toutes les factures une seule fois
  const fetchAllBills = async () => {
    try {
      const res = await axios.get("/api/Bills/getAllBills");
      setAllBills(res.data || []);
    } catch (err) {
      console.error("❌ Erreur chargement factures:", err);
    }
  };

  // FONCTION POUR CHARGER LES FACTURES D'UNE CLÔTURE
  const fetchBillsForClosure = async (closure) => {
    try {
      // Utiliser les factures déjà chargées
      const allBillsData = allBills;
      
      // Filtrer les factures par date et localisation de la clôture
      const closureDate = new Date(closure.date).toISOString().split('T')[0];
      const closureLocationId = getLocationId(closure);
      
      let filteredBills = allBillsData.filter(bill => {
        const billDate = new Date(bill.date || bill.createdAt).toISOString().split('T')[0];
        const billLocationId = getLocationId(bill);
        
        return billDate === closureDate && billLocationId === closureLocationId;
      });
      
      // Filtrer par heure pour la passation
      if (closure.closureType === "passation") {
        const closureTime = new Date(closure.date);
        filteredBills = filteredBills.filter(bill => {
          const billTime = new Date(bill.date || bill.createdAt);
          return billTime <= closureTime;
        });
      } else if (closure.closureType === "fin_journee") {
        // Pour fin de journée, prendre toutes les ventes après la passation
        const passationForDay = closures.find(c => 
          c.closureType === "passation" && 
          new Date(c.date).toISOString().split('T')[0] === closureDate &&
          getLocationId(c) === closureLocationId
        );
        
        if (passationForDay) {
          const passationTime = new Date(passationForDay.date);
          filteredBills = filteredBills.filter(bill => {
            const billTime = new Date(bill.date || bill.createdAt);
            return billTime > passationTime;
          });
        }
      }
      
      setBillsForSelectedClosure(filteredBills);
      return filteredBills;
    } catch (err) {
      console.error("❌ Erreur chargement factures:", err);
      return [];
    }
  };

  const fetchAllClosures = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/CloseDay/getAllClosures");
      const allClosures = res.data || [];
      
      const sortedClosures = allClosures.sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      setClosures(sortedClosures);
      setFilteredClosures(sortedClosures);
    } catch (err) {
      console.error("❌ Erreur chargement clôtures:", err);
      setError("Erreur de chargement des clôtures");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLocations = async () => {
    try {
      const res = await axios.get("/api/Locationspin/getAllLocations");
      setLocations(res.data || []);
    } catch (err) {
      console.error("❌ Erreur chargement localisations:", err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLocationName = (closure) => {
    if (closure.locationName) return closure.locationName;
    
    if (closure.localisation) {
      if (closure.localisation.name) return closure.localisation.name;
      if (closure.localisation.$oid) {
        const loc = locations.find(l => l._id === closure.localisation.$oid);
        return loc ? loc.name : `Localisation ${closure.localisation.$oid}`;
      }
      if (typeof closure.localisation === 'string') {
        const loc = locations.find(l => l._id === closure.localisation);
        return loc ? loc.name : `Localisation ${closure.localisation}`;
      }
    }
    
    if (closure.locationId) {
      const loc = locations.find(l => l._id === closure.locationId);
      return loc ? loc.name : `Localisation ${closure.locationId}`;
    }
    
    return "Localisation inconnue";
  };

  const getLocationId = (closure) => {
    if (closure.locationId) return String(closure.locationId);
    
    if (closure.localisation) {
      if (closure.localisation.$oid) return String(closure.localisation.$oid);
      if (closure.localisation._id) return String(closure.localisation._id);
      if (typeof closure.localisation === 'string') return String(closure.localisation);
    }
    
    return null;
  };

  // CORRIGÉ : Récupérer le total TTC correctement
  const getClosureTotalTTC = (closure) => {
    // 1. Essayer de récupérer le champ totalSales (qui est TTC dans votre base)
    if (closure.totalSales !== undefined && closure.totalSales !== null) {
      return closure.totalSales; // C'est déjà en TTC
    }
    // 2. Si on a total (supposé TTC)
    else if (closure.total !== undefined) {
      return closure.total;
    }
    // 3. Sinon calculer à partir des factures
    else {
      return 0;
    }
  };

  // CORRIGÉ : Calculer le total HT à partir du TTC
  const getClosureTotalHT = (closure) => {
    const totalTTC = getClosureTotalTTC(closure);
    return totalTTC / 1.07; // TVA 7%
  };

  const getClosuresGroupedByDate = (closuresList) => {
  const grouped = {};
  
  closuresList.forEach(closure => {
    const dateKey = new Date(closure.date).toISOString().split('T')[0];
    const dateFormatted = formatDate(closure.date);
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        date: dateFormatted,
        dateKey: dateKey,
        closures: [],
        totals: {
          passation: 0,
          apres_passation: 0, // Changé de fin_journee à apres_passation
          totalSalesTTC: 0,
          totalBills: 0
        }
      };
    }
    
    grouped[dateKey].closures.push(closure);
    
    const closureTotalTTC = getClosureTotalTTC(closure);
    
    if (closure.closureType === "passation") {
      grouped[dateKey].totals.passation += closureTotalTTC;
    } else if (closure.closureType === "fin_journee") {
      grouped[dateKey].totals.apres_passation += closureTotalTTC;
      // CORRECTION : Le total journée = seulement après passation
      grouped[dateKey].totals.totalSalesTTC += closureTotalTTC;
    }
    
    grouped[dateKey].totals.totalBills += Number(closure.totalBills || 0);
  });
  
  return Object.values(grouped).sort((a, b) => 
    new Date(b.dateKey) - new Date(a.dateKey)
  );
};

  const applyFilters = () => {
    let filtered = [...closures];

    if (searchDate) {
      filtered = filtered.filter(closure => {
        const closureDate = new Date(closure.date).toISOString().split('T')[0];
        return closureDate === searchDate;
      });
    }

    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(closure => {
        const closureDate = new Date(closure.date).toISOString().split('T')[0];
        return closureDate >= dateRange.start && closureDate <= dateRange.end;
      });
    }

    if (selectedLocation) {
      filtered = filtered.filter(closure => {
        const locationId = getLocationId(closure);
        return locationId === selectedLocation;
      });
    }

    setFilteredClosures(filtered);
  };

  const resetFilters = () => {
    setSearchDate("");
    setDateRange({ start: "", end: "" });
    setSelectedLocation("");
    setFilteredClosures(closures);
  };

  const downloadCSV = () => {
    if (filteredClosures.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }

    const headers = [
      "Date", "Heure", "Type", "Localisation", 
      "Factures", "Total TTC (DT)", "Total HT (DT)", "Utilisateur", "Statut Paiement (TTC)"
    ];

    const csvData = filteredClosures.map(closure => {
      const totalTTC = getClosureTotalTTC(closure);
      const totalHT = getClosureTotalHT(closure);
      
      // Calculer les stats de paiement pour cette clôture
      const closureDate = new Date(closure.date).toISOString().split('T')[0];
      const closureLocationId = getLocationId(closure);
      
      const billsForClosure = allBills.filter(bill => {
        const billDate = new Date(bill.date || bill.createdAt).toISOString().split('T')[0];
        const billLocationId = getLocationId(bill);
        return billDate === closureDate && billLocationId === closureLocationId;
      });
      
      const paymentStats = calculatePaymentStatsFromBills(billsForClosure);
      const paymentSummary = Object.entries(paymentStats)
        .map(([method, amount]) => `${method}: ${amount.toFixed(2)}`)
        .join("; ");

      return [
        formatDate(closure.date),
        formatTime(closure.date),
        closure.closureType === "passation" ? "Passation" : "Fin de Journée",
        getLocationName(closure),
        closure.totalBills || 0,
        totalTTC.toFixed(2),
        totalHT.toFixed(2),
        closure.userId?.name || "N/A",
        paymentSummary || "N/A"
      ];
    });

    const csvContent = [
      headers.join(";"),
      ...csvData.map(row => row.join(";"))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `clotures_ttc_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // FONCTION CORRIGÉE POUR GÉNÉRER LE PDF
 // FONCTION CORRIGÉE POUR GÉNÉRER LE PDF
// FONCTION CORRIGÉE POUR GÉNÉRER LE PDF
// FONCTION SIMPLIFIÉE POUR GÉNÉRER LE PDF
// FONCTION CORRIGÉE POUR GÉNÉRER LE PDF
// FONCTION CORRIGÉE POUR GÉNÉRER LE PDF
// FONCTION CORRIGÉE POUR GÉNÉRER LE PDF
// FONCTION CORRIGÉE POUR GÉNÉRER LE PDF
const generateDetailedPDF = async (closure) => {
  try {
    const closureDate = new Date(closure.date).toISOString().split('T')[0];
    const closureLocationId = getLocationId(closure);
    
    // Filtrer toutes les factures pour cette journée et localisation
    const allBillsForDay = allBills.filter(bill => {
      const billDate = new Date(bill.date || bill.createdAt).toISOString().split('T')[0];
      const billLocationId = getLocationId(bill);
      return billDate === closureDate && billLocationId === closureLocationId;
    });
    
    if (allBillsForDay.length === 0) {
      alert("Aucune donnée de vente disponible pour générer le PDF.");
      return;
    }

    const dateText = formatDate(closure.date);
    const timeText = formatTime(closure.date);
    const locationName = getLocationName(closure);

    const tableData = [];
    
    // Trier les factures par heure
    const sortedBills = [...allBillsForDay].sort((a, b) => 
      new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt)
    );

    // En-tête du tableau
    tableData.push([
      { text: "Heure", style: "tableHeader", bold: true },
      { text: "Table", style: "tableHeader", bold: true },
      { text: "Produit", style: "tableHeader", bold: true },
      { text: "Qté", style: "tableHeader", bold: true },
      { text: "Prix TTC", style: "tableHeader", bold: true },
      { text: "Total TTC", style: "tableHeader", bold: true }
    ]);

    let totalPassationTTC = 0;
    let totalApresPassationTTC = 0;
    let totalGeneralTTC = 0;
    let totalRemises = 0;
    let passationTime = null;
    
    // Trouver l'heure de passation si elle existe
    if (closure.closureType === "fin_journee") {
      const passationForDay = closures.find(c => 
        c.closureType === "passation" && 
        new Date(c.date).toISOString().split('T')[0] === closureDate &&
        getLocationId(c) === closureLocationId
      );
      
      if (passationForDay) {
        passationTime = new Date(passationForDay.date);
      }
    }

    let passationLineAdded = false;
    
    sortedBills.forEach(bill => {
      const billTime = new Date(bill.date || bill.createdAt).toLocaleTimeString('fr-FR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      
      const billTable = bill.tableNumber || "N/A";
      const billDate = new Date(bill.date || bill.createdAt);
      
      // Calculer le montant SANS remise
      let billTotalSansRemise = 0;
      if (bill.items && bill.items.length > 0) {
        billTotalSansRemise = bill.items.reduce((total, item) => {
          const itemPriceTTC = item.price || 0; // Prix déjà TTC
          const quantity = item.quantity || 0;
          return total + (itemPriceTTC * quantity);
        }, 0);
      }
      
      // Appliquer la remise si elle existe
      const discountPercent = bill.discount || 0;
      let billTotalTTC = billTotalSansRemise;
      let remiseFacture = 0;
      
      if (discountPercent > 0) {
        remiseFacture = billTotalSansRemise * (discountPercent / 100);
        billTotalTTC = billTotalSansRemise - remiseFacture;
        totalRemises += remiseFacture;
      }
      
      // Ajouter une ligne de séparation si c'est une fin de journée avec passation
      if (closure.closureType === "fin_journee" && passationTime && !passationLineAdded && billDate > passationTime) {
        tableData.push([
          { 
            text: `--- PASSATION ---`, 
            colSpan: 6, 
            style: "separationLine",
            bold: true,
            alignment: "center"
          },
          {}, {}, {}, {}, {}
        ]);
        passationLineAdded = true;
      }

      if (bill.items && bill.items.length > 0) {
        // Afficher chaque article
        bill.items.forEach((item, index) => {
          const itemPriceTTC = item.price || 0; // Prix déjà TTC
          const quantity = item.quantity || 0;
          const itemTotalTTC = itemPriceTTC * quantity;

          tableData.push([
            index === 0 ? billTime : "",
            index === 0 ? billTable : "",
            item.name || "Produit",
            quantity,
            `${itemPriceTTC.toFixed(2)} DT`,
            `${itemTotalTTC.toFixed(2)} DT`
          ]);
        });

        // Ligne de total pour la facture
        if (discountPercent > 0) {
          tableData.push([
            { text: `Sous-total`, colSpan: 5, alignment: "right" },
            {},
            {},
            {},
            {},
            { 
              text: `${billTotalSansRemise.toFixed(2)} DT`, 
              bold: true
            }
          ]);
          
          tableData.push([
            { text: `Remise ${discountPercent}%`, colSpan: 5, alignment: "right", color: "#d32f2f" },
            {},
            {},
            {},
            {},
            { 
              text: `-${remiseFacture.toFixed(2)} DT`, 
              bold: true,
              color: "#d32f2f"
            }
          ]);
        }
        
        tableData.push([
          { text: `Total facture`, colSpan: 5, alignment: "right", bold: true },
          {},
          {},
          {},
          {},
          { 
            text: `${billTotalTTC.toFixed(2)} DT`, 
            bold: true,
            fontSize: 10
          }
        ]);

        // Ajouter au bon total selon l'heure
        if (passationTime) {
          if (billDate <= passationTime) {
            totalPassationTTC += billTotalTTC;
          } else {
            totalApresPassationTTC += billTotalTTC;
          }
        }

        totalGeneralTTC += billTotalTTC;

        // Ligne séparatrice
        tableData.push([
          { text: "", colSpan: 6, margin: [0, 2, 0, 2], border: [false, false, false, true], borderColor: '#e0e0e0' }
        ]);
      }
    });

    const content = [
      { 
        text: `RAPPORT ${closure.closureType === "passation" ? "PASSATION" : "FIN DE JOURNÉE"}`, 
        style: "header" 
      },
      { text: `Date : ${dateText}`, margin: [0, 5, 0, 2] },
      { text: `Heure : ${timeText}`, margin: [0, 0, 0, 2] },
      { text: `Localisation : ${locationName}`, margin: [0, 0, 0, 15] },
      { text: `Utilisateur : ${closure.userId?.name || "N/A"}`, margin: [0, 0, 0, 2] },
    ];

    content.push(
      { text: "DÉTAIL DES VENTES", style: "subheader", margin: [0, 0, 0, 10] },
      {
        table: {
          headerRows: 1,
          widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto'],
          body: tableData
        }
      }
    );

    // TOTAUX AVEC LIGNE POUR LES REMISES
    if (closure.closureType === "passation") {
      content.push(
        { 
          text: `TOTAL VENTES SANS REMISES : ${(totalGeneralTTC + totalRemises).toFixed(2)} DT`, 
          style: "subtotal", 
          margin: [0, 15, 0, 5],
          alignment: "right"
        },
        { 
          text: `TOTAL REMISES : -${totalRemises.toFixed(2)} DT`, 
          style: "subtotal", 
          margin: [0, 5, 0, 5],
          alignment: "right",
          color: "#d32f2f"
        },
        { 
          text: `TOTAL PASSATION TTC : ${totalGeneralTTC.toFixed(2)} DT`, 
          style: "total", 
          margin: [0, 5, 0, 5],
          alignment: "right"
        }
        // CORRECTION 1: Retiré la ligne TOTAL PASSATION HT
      );
    } else if (closure.closureType === "fin_journee") {
      if (passationTime) {
        content.push(
          { 
            text: `PASSATION TTC : ${totalPassationTTC.toFixed(2)} DT`, 
            style: "subtotal", 
            margin: [0, 15, 0, 5],
            alignment: "right"
          },
          { 
            text: `APRÈS PASSATION TTC : ${totalApresPassationTTC.toFixed(2)} DT`, 
            style: "subtotal", 
            margin: [0, 5, 0, 5],
            alignment: "right"
          },
          { 
            text: `TOTAL VENTES SANS REMISES : ${(totalGeneralTTC + totalRemises).toFixed(2)} DT`, 
            style: "subtotal", 
            margin: [0, 10, 0, 5],
            alignment: "right"
          },
          { 
            text: `TOTAL REMISES : -${totalRemises.toFixed(2)} DT`, 
            style: "subtotal", 
            margin: [0, 5, 0, 5],
            alignment: "right",
            color: "#d32f2f"
          },
          { 
            text: `TOTAL JOURNÉE TTC : ${totalGeneralTTC.toFixed(2)} DT`, 
            style: "total", 
            margin: [0, 5, 0, 5],
            alignment: "right"
          }
          // CORRECTION 1: Retiré la ligne TOTAL JOURNÉE HT
        );
      } else {
        content.push(
          { 
            text: `TOTAL VENTES SANS REMISES : ${(totalGeneralTTC + totalRemises).toFixed(2)} DT`, 
            style: "subtotal", 
            margin: [0, 15, 0, 5],
            alignment: "right"
          },
          { 
            text: `TOTAL REMISES : -${totalRemises.toFixed(2)} DT`, 
            style: "subtotal", 
            margin: [0, 5, 0, 5],
            alignment: "right",
            color: "#d32f2f"
          },
          { 
            text: `TOTAL JOURNÉE TTC : ${totalGeneralTTC.toFixed(2)} DT`, 
            style: "total", 
            margin: [0, 5, 0, 5],
            alignment: "right"
          }
          // CORRECTION 1: Retiré la ligne TOTAL JOURNÉE HT
        );
      }
    }

    // FONCTION POUR DÉCOMPOSER LES PAIEMENTS COMBINÉS CORRECTEMENT
    const calculateDetailedPaymentStats = (bills) => {
      const paymentDetails = {
        Espece: 0,
        TPE: 0,
        "T.Resto": 0,
        Glovo: 0,
        Yassir: 0,
        Autre: 0
        // CORRECTION 2: Retiré "Combiné (décomposé)" pour éviter les doublons
      };
      
      bills.forEach(bill => {
        // Calculer le total TTC de la facture (avec remise)
        let billTotalTTC = 0;
        let billTotalSansRemise = 0;
        
        if (bill.items && bill.items.length > 0) {
          billTotalSansRemise = bill.items.reduce((total, item) => {
            return total + ((item.price || 0) * (item.quantity || 0));
          }, 0);
        }
        
        const discountPercent = bill.discount || 0;
        if (discountPercent > 0) {
          billTotalTTC = billTotalSansRemise * (1 - discountPercent / 100);
        } else {
          billTotalTTC = billTotalSansRemise;
        }
        
        // CORRECTION : Utiliser les données réelles des paiements combinés
        if (bill.combinedPayment === true && bill.combinedPaymentsDetail && Array.isArray(bill.combinedPaymentsDetail)) {
          // Paiement combiné avec détails disponibles
          console.log(`📋 Facture ${bill.billNumber}: Paiement combiné détaillé`, bill.combinedPaymentsDetail);
          
          bill.combinedPaymentsDetail.forEach(payment => {
            const method = payment.method || "Espece";
            const amount = payment.amount || 0;
            
            // Normaliser le nom de la méthode
            const normalizedMethod = method === "T.Resto" || method === "Ticket Resto" ? "T.Resto" :
                                   method === "Espece" || method === "Espèce" ? "Espece" :
                                   method === "TPE" || method === "Carte" || method === "CB" ? "TPE" :
                                   method === "Yassir"|| method === "YASSIR"? "Yassir":
                                   method === "Glovo" ? "Glovo" : "Autre";
                                   
            
            if (paymentDetails[normalizedMethod] !== undefined) {
              paymentDetails[normalizedMethod] += amount;
            } else {
              paymentDetails[normalizedMethod] = amount;
            }
          });
        } 
        else if (bill.paymentMethod === "Combiné" || bill.paymentMethod === "combiné") {
          // Paiement combiné sans détails dans la base - utiliser les infos disponibles
          console.log(`⚠️ Facture ${bill.billNumber}: Paiement combiné sans détails, paymentMethod: ${bill.paymentMethod}`);
          
          // Essayer de récupérer les détails d'une autre manière
          if (bill.paymentMethodsSummary) {
            // Si on a un résumé des paiements
            try {
              const summary = typeof bill.paymentMethodsSummary === 'string' 
                ? JSON.parse(bill.paymentMethodsSummary) 
                : bill.paymentMethodsSummary;
              
              if (summary && Array.isArray(summary)) {
                summary.forEach(payment => {
                  const method = payment.method || "Espece";
                  const amount = payment.amount || 0;
                  
                  const normalizedMethod = method === "T.Resto" || method === "Ticket Resto" ? "T.Resto" :
                                         method === "Espece" || method === "Espèce" ? "Espece" :
                                         method === "TPE" || method === "Carte" ? "TPE" :
                                         method === "Yassir"|| method === "YASSIR"? "Yassir":
                                         method === "Glovo" ? "Glovo" : "Autre";
                  
                  if (paymentDetails[normalizedMethod] !== undefined) {
                    paymentDetails[normalizedMethod] += amount;
                  } else {
                    paymentDetails[normalizedMethod] = amount;
                  }
                });
              }
            } catch (error) {
              console.error("Erreur parsing paymentMethodsSummary:", error);
            }
          }
          
          // Si toujours pas de détails, répartir proportionnellement
          if (!bill.paymentMethodsSummary) {
            console.log(`ℹ️ Facture ${bill.billNumber}: Répartition par défaut 50% Espèces, 50% TPE`);
            // Répartition par défaut : 50% espèces, 50% TPE
            paymentDetails["Espece"] += billTotalTTC * 0.5;
            paymentDetails["TPE"] += billTotalTTC * 0.5;
          }
        } 
        else {
          // Paiement normal (non combiné)
          const method = bill.paymentMethod || "Autre";
          const normalizedMethod = method === "T.Resto" || method === "Ticket Resto" ? "T.Resto" :
                                 method === "Espece" || method === "Espèce" ? "Espece" :
                                 method === "TPE" || method === "Carte" ? "TPE" :
                                 method === "Glovo" ? "Glovo" : "Autre";
          
          if (paymentDetails[normalizedMethod] !== undefined) {
            paymentDetails[normalizedMethod] += billTotalTTC;
          } else {
            paymentDetails[normalizedMethod] = billTotalTTC;
          }
        }
      });
      
      return paymentDetails;
    };

    // CALCULER LA RÉPARTITION DÉTAILLÉE DES PAIEMENTS
    const paymentDetails = calculateDetailedPaymentStats(sortedBills);
    
    const paymentRows = Object.entries(paymentDetails)
      .filter(([method, amount]) => amount > 0)
      .map(([method, amount]) => [
        method === "T.Resto" ? "TICKET RESTO" : 
        method === "Espece" ? "ESPÈCES" :
        method === "TPE" ? "CARTE BANCAIRE" :
        method === "Glovo" ? "GLOVO" :
        method === "Yassir" ? "YASSIR" :
        method === "Autre" ? "AUTRE" :
        method.toUpperCase(),
        `${amount.toFixed(2)} DT`
      ]);

    const totalPaymentsTTC = Object.values(paymentDetails).reduce((sum, amount) => 
      sum + amount, 0
    );

    // COMPTE RENDU DES PAIEMENTS COMBINÉS
    const combinedBills = sortedBills.filter(bill => 
      bill.combinedPayment === true || bill.paymentMethod === "Combiné" || bill.paymentMethod === "combiné"
    );

    if (paymentRows.length > 0) {
      content.push(
        { text: "RÉPARTITION DÉTAILLÉE DES PAIEMENTS", style: "subheader", margin: [0, 20, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: ["*", "auto"],
            body: [
              ["Moyen de paiement", "Montant TTC"],
              ...paymentRows
            ],
          },
          margin: [0, 0, 0, 15],
        },
        { 
          text: `TOTAL PAIEMENTS : ${totalPaymentsTTC.toFixed(2)} DT`, 
          style: "total", 
          margin: [0, 5, 0, 5],
          alignment: "right"
        }
      );
      
      // Ajouter le détail des paiements combinés uniquement s'il y en a
      if (combinedBills.length > 0) {
        content.push(
          { text: "DÉTAIL DES PAIEMENTS COMBINÉS", style: "subheader", margin: [0, 15, 0, 10] }
        );
        
        const combinedTableData = [
          [
            { text: "Facture", style: "tableHeader", bold: true },
            { text: "Méthodes", style: "tableHeader", bold: true },
            { text: "Montants", style: "tableHeader", bold: true }
          ]
        ];
        
        combinedBills.forEach(bill => {
          if (bill.combinedPaymentsDetail && Array.isArray(bill.combinedPaymentsDetail)) {
            bill.combinedPaymentsDetail.forEach((payment, index) => {
              combinedTableData.push([
                index === 0 ? `#${bill.billNumber || bill._id}` : "",
                payment.method || "Non spécifié",
                `${(payment.amount || 0).toFixed(2)} DT`
              ]);
            });
            // Ligne séparatrice
            combinedTableData.push([
              { text: "", colSpan: 3, border: [false, false, false, true], borderColor: '#e0e0e0', margin: [0, 2, 0, 2] }
            ]);
          } else {
            combinedTableData.push([
              `#${bill.billNumber || bill._id}`,
              "Détails non disponibles",
              `${calculateBillTotalTTC(bill).toFixed(2)} DT`
            ]);
            combinedTableData.push([
              { text: "", colSpan: 3, border: [false, false, false, true], borderColor: '#e0e0e0', margin: [0, 2, 0, 2] }
            ]);
          }
        });
        
        content.push({
          table: {
            headerRows: 1,
            widths: ["auto", "*", "auto"],
            body: combinedTableData
          },
          margin: [0, 0, 0, 15],
          fontSize: 8
        });
      }
    }

    // Vérifier la cohérence des totaux
    if (Math.abs(totalGeneralTTC - totalPaymentsTTC) > 0.01) {
      content.push(
        { 
          text: `Note : Petite différence de ${Math.abs(totalGeneralTTC - totalPaymentsTTC).toFixed(2)} DT`, 
          style: "note", 
          margin: [0, 10, 0, 5],
          alignment: "center",
          fontSize: 9,
          color: "#666"
        }
      );
    }

    // INFORMATION SUR LES REMISES
    if (totalRemises > 0) {
      const pourcentageRemises = ((totalRemises / (totalGeneralTTC + totalRemises)) * 100).toFixed(1);
      content.push(
        { 
          text: `Total des remises accordées : ${totalRemises.toFixed(2)} DT (${pourcentageRemises}% des ventes)`, 
          style: "note", 
          margin: [0, 15, 0, 5],
          alignment: "center",
          fontSize: 9,
          color: "#d32f2f",
          bold: true
        }
      );
    }

    const docDefinition = {
      content: content,
      styles: {
        header: { 
          fontSize: 18, 
          bold: true, 
          alignment: "center", 
          margin: [0, 0, 0, 10],
          color: "#2c3e50"
        },
        subheader: { 
          fontSize: 14, 
          bold: true, 
          margin: [0, 0, 0, 5],
          color: "#3498db"
        },
        tableHeader: { 
          fontSize: 9, 
          bold: true, 
          fillColor: "#f8f9fa",
          color: "#2c3e50"
        },
        subtotal: { 
          fontSize: 11, 
          bold: true, 
          color: "#3498db" 
        },
        total: { 
          fontSize: 13, 
          bold: true, 
          color: "#e74c3c" 
        },
        note: { 
          fontSize: 9, 
          color: "#666" 
        },
        separationLine: {
          fontSize: 10,
          bold: true,
          color: "#e74c3c",
          margin: [0, 10, 0, 10],
          fillColor: "#fff3cd"
        }
      },
      pageMargins: [20, 20, 20, 20],
      defaultStyle: { 
        fontSize: 9,
        color: "#2c3e50"
      }
    };

    pdfMake.createPdf(docDefinition).open();

  } catch (error) {
    console.error("❌ Erreur génération PDF:", error);
    alert("Erreur lors de la génération du PDF");
  }
};
  const showClosureDetails = async (closure) => {
    setSelectedClosure(closure);
    setShowDetails(true);
    // Charger les factures pour cette clôture
    await fetchBillsForClosure(closure);
  };

  const groupedClosures = getClosuresGroupedByDate(filteredClosures);

  return (
    <div className="global-closure-container">
      <div className="global-header">
        <h1>📊 VUE GLOBALE DES CLÔTURES (TTC)</h1>
        <div className="stats-summary">
          <div className="stat-card">
            <span className="stat-value">{closures.length}</span>
            <span className="stat-label">Clôtures totales</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">
              {closures.filter(c => c.closureType === "fin_journee").length}
            </span>
            <span className="stat-label">Fins de journée</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">
              {new Set(closures.map(c => getLocationId(c))).size}
            </span>
            <span className="stat-label">Localisations</span>
          </div>
        </div>
      </div>

      {/* Section Filtres EN LIGNE */}
      <div className="filters-section">
        <div className="filters-inline-container">
          {/* Plage de dates */}
          <div className="date-range-group">
            <label>Dates:</label>
            <div className="date-range-inputs">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                className="filter-input"
                placeholder="Du"
              />
              <span className="date-range-separator">→</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                className="filter-input"
                placeholder="Au"
              />
            </div>
          </div>

          {/* Localisation */}
          <div className="filter-inline-group">
            <label>Localisation:</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="filter-select"
            >
              <option value="">Toutes</option>
              {locations.map(location => (
                <option key={location._id} value={location._id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="filter-actions-inline">
          <button onClick={applyFilters} className="apply-btn">
            🔍 Appliquer filtres
          </button>
          <button onClick={resetFilters} className="reset-btn">
            🔄 Réinitialiser
          </button>
          <button 
            onClick={downloadCSV} 
            className="export-btn" 
            disabled={filteredClosures.length === 0}
            title={`Exporter ${filteredClosures.length} clôture${filteredClosures.length !== 1 ? 's' : ''}`}
          >
            📥 Exporter CSV TTC ({filteredClosures.length})
          </button>
        </div>
      </div>

{/* Résultats */}
<div className="results-section">
  <div className="results-header">
    <h3>
      📋 Résultats: {filteredClosures.length} clôture{filteredClosures.length !== 1 ? 's' : ''}
      {selectedLocation && ` pour ${locations.find(l => l._id === selectedLocation)?.name}`}
    </h3>
  </div>

  {loading ? (
    <div className="loading">Chargement en cours...</div>
  ) : error ? (
    <div className="error-message">{error}</div>
  ) : groupedClosures.length === 0 ? (
    <div className="no-results">
      <p>❌ Aucune clôture trouvée avec les critères sélectionnés</p>
    </div>
  ) : (
    groupedClosures.map((dayGroup) => {
      // CORRECTION: Obtenir la localisation du premier groupe de clôtures
      const firstClosureLocationId = dayGroup.closures.length > 0 ? 
        getLocationId(dayGroup.closures[0]) : null;
      
      // 1. Trouver toutes les factures pour cette journée et localisation
      const dayBills = allBills.filter(bill => {
        const billDate = new Date(bill.date || bill.createdAt).toISOString().split('T')[0];
        const billLocationId = getLocationId(bill);
        return billDate === dayGroup.dateKey && 
               billLocationId === firstClosureLocationId;
      });
      
      // 2. Trouver l'heure de passation pour cette journée
      const passationForDay = dayGroup.closures.find(c => 
        c.closureType === "passation"
      );
      
      let passationTime = null;
      if (passationForDay) {
        passationTime = new Date(passationForDay.date);
      }
      
      // 3. Calculer les totaux comme dans le PDF
      let totalPassationTTC = 0;
      let totalApresPassationTTC = 0;
      let totalRemises = 0;
      
      dayBills.forEach(bill => {
        // Calculer le montant SANS remise
        let billTotalSansRemise = 0;
        if (bill.items && bill.items.length > 0) {
          billTotalSansRemise = bill.items.reduce((total, item) => {
            const itemPriceTTC = item.price || 0; // Prix déjà TTC
            const quantity = item.quantity || 0;
            return total + (itemPriceTTC * quantity);
          }, 0);
        }
        
        // Appliquer la remise si elle existe
        const discountPercent = bill.discount || 0;
        let billTotalTTC = billTotalSansRemise;
        let remiseFacture = 0;
        
        if (discountPercent > 0) {
          remiseFacture = billTotalSansRemise * (discountPercent / 100);
          billTotalTTC = billTotalSansRemise - remiseFacture;
          totalRemises += remiseFacture;
        }
        
        const billDate = new Date(bill.date || bill.createdAt);
        
        // Répartir selon l'heure de passation
        if (passationTime) {
          if (billDate <= passationTime) {
            totalPassationTTC += billTotalTTC;
          } else {
            totalApresPassationTTC += billTotalTTC;
          }
        } else {
          // Si pas de passation, tout va dans après passation
          totalApresPassationTTC += billTotalTTC;
        }
      });
      
      // 4. Calculer le total journée
      const totalJourneeTTC = totalPassationTTC + totalApresPassationTTC;
      const totalSansRemises = totalJourneeTTC + totalRemises;
      
      return (
        <div key={dayGroup.dateKey} className="day-group-table">
          <div className="day-header">
            <h4>{dayGroup.date}</h4>
            <div className="day-totals">
              <span className="day-total-item">
                <strong>{dayGroup.closures.length}</strong> Clôture{dayGroup.closures.length !== 1 ? 's' : ''}
              </span>
              <span className="day-total-item">
                <strong>{dayBills.length}</strong> Factures
              </span>
              <span className="day-total-item">
                <strong>{totalJourneeTTC.toFixed(2)} DT TTC</strong> TOTAL JOURNÉE
              </span>
              {passationTime && (
                <>
                  <span className="day-total-item">
                    Passation : <strong>{totalPassationTTC.toFixed(2)} DT</strong>
                  </span>
                  <span className="day-total-item">
                    Après passation : <strong>{totalApresPassationTTC.toFixed(2)} DT</strong>
                  </span>
                </>
              )}
              {totalRemises > 0 && (
                <span className="day-total-item" style={{color: '#d32f2f'}}>
                  Remises: <strong>-{totalRemises.toFixed(2)} DT</strong>
                </span>
              )}
            </div>
          </div>

          <div className="closures-table-container">
            <table className="closures-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Heure</th>
                  <th>Type</th>
                  <th>Localisation</th>
                  <th>Factures</th>
                  <th>Total TTC (DT)</th>
                  <th>Utilisateur</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dayGroup.closures.map((closure) => {
                  // CORRECTION: Calculer le total TTC correctement (sans TVA supplémentaire)
                  let totalTTC = 0;
                  let totalHT = 0;
                  
                  // Essayer de récupérer le montant depuis les factures
                  const closureBills = dayBills.filter(bill => {
                    const billDate = new Date(bill.date || bill.createdAt);
                    const closureDate = new Date(closure.date);
                    
                    if (closure.closureType === "passation") {
                      return billDate <= closureDate;
                    } else if (closure.closureType === "fin_journee") {
                      if (passationTime) {
                        return billDate > passationTime;
                      } else {
                        return billDate > closureDate; // approximation si pas de passation
                      }
                    }
                    return false;
                  });
                  
                  // Calculer le total TTC pour cette clôture
                  closureBills.forEach(bill => {
                    let billTotalSansRemise = 0;
                    if (bill.items && bill.items.length > 0) {
                      billTotalSansRemise = bill.items.reduce((total, item) => {
                        return total + ((item.price || 0) * (item.quantity || 0));
                      }, 0);
                    }
                    
                    const discountPercent = bill.discount || 0;
                    if (discountPercent > 0) {
                      totalTTC += billTotalSansRemise * (1 - discountPercent / 100);
                    } else {
                      totalTTC += billTotalSansRemise;
                    }
                  });
                  
                  // Si pas de factures trouvées, utiliser la méthode existante
                  if (totalTTC === 0) {
                    totalTTC = getClosureTotalTTC(closure);
                  }
                  
                  // Calculer HT correctement (TTC / 1.07)
                  totalHT = totalTTC / 1.07;
                  
                  // Compter les factures pour cette clôture
                  const billCount = closureBills.length;
                  
                  return (
                    <tr key={closure._id}>
                      <td className="date-cell">{formatDate(closure.date)}</td>
                      <td className="time-cell">{formatTime(closure.date)}</td>
                      <td>
                        <span className={`type-cell ${closure.closureType === "passation" ? "type-passation" : "type-fin_journee"}`}>
                          {closure.closureType === "passation" ? "🔄 PASSATION" : "🏁 FIN JOURNÉE"}
                        </span>
                      </td>
                      <td className="location-cell">{getLocationName(closure)}</td>
                      <td className="bills-cell">{billCount || 0}</td>
                      <td className="amount-cell">
                        <div className="amount-display">
                          <span className="amount-ttc">
                            {totalTTC.toFixed(2)} DT
                          </span>
                          <div className="amount-ht" style={{fontSize: '0.8em', color: '#666'}}>
                            HT: {totalHT.toFixed(2)} DT
                          </div>
                        </div>
                      </td>
                      <td className="user-cell">{closure.userId?.name || "N/A"}</td>
                      <td className="actions-cell">
                        <button 
                          className="view-details-btn"
                          onClick={() => showClosureDetails(closure)}
                        >
                          👁️ Détails
                        </button>
                        <button 
                          className="view1-details-btn"
                          onClick={() => generateDetailedPDF(closure)}
                          title="Générer PDF détaillé"
                        >
                          📄 PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    })
  )}
</div>

      {/* Modal pour afficher les détails */}
      {showDetails && selectedClosure && (
        <div className="details-modal" onClick={() => setShowDetails(false)}>
          <div className="details-content" onClick={(e) => e.stopPropagation()}>
            <div className="details-header">
              <h3>
                {selectedClosure.closureType === "passation" ? "🔄 Passation" : "🏁 Fin de Journée"} - 
                {formatDate(selectedClosure.date)} {formatTime(selectedClosure.date)}
              </h3>
              <button className="close-btn" onClick={() => setShowDetails(false)}>×</button>
            </div>
            
            <div className="details-grid">
              <div className="detail-item">
                <div className="detail-label">Localisation</div>
                <div className="detail-value">{getLocationName(selectedClosure)}</div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Type de clôture</div>
                <div className="detail-value">
                  {selectedClosure.closureType === "passation" ? "Passation" : "Fin de Journée"}
                </div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Nombre de factures</div>
                <div className="detail-value">{selectedClosure.totalBills || 0}</div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Total des ventes HT</div>
                <div className="detail-value">{getClosureTotalHT(selectedClosure).toFixed(2)} DT</div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Total des ventes TTC</div>
                <div className="detail-value">{getClosureTotalTTC(selectedClosure).toFixed(2)} DT</div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Utilisateur</div>
                <div className="detail-value">{selectedClosure.userId?.name || "N/A"}</div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Date de création</div>
                <div className="detail-value">
                  {new Date(selectedClosure.createdAt).toLocaleString('fr-FR')}
                </div>
              </div>
            </div>
            
            {billsForSelectedClosure.length > 0 && (
              <div className="bills-details-section">
                <h4>📊 Détails des factures ({billsForSelectedClosure.length})</h4>
                <div className="bills-summary">
                  {billsForSelectedClosure.slice(0, 5).map((bill, index) => (
                    <div key={bill._id} className="bill-summary-row">
                      <span className="bill-time">
                        {new Date(bill.date || bill.createdAt).toLocaleTimeString('fr-FR')}
                      </span>
                      <span className="bill-table">Table {bill.tableNumber || "N/A"}</span>
                      <span className="bill-items">{bill.items?.length || 0} articles</span>
                      <span className="bill-total">{calculateBillTotalTTC(bill).toFixed(2)} DT TTC</span>
                    </div>
                  ))}
                  {billsForSelectedClosure.length > 5 && (
                    <div className="more-bills">
                      + {billsForSelectedClosure.length - 5} autre{billsForSelectedClosure.length - 5 !== 1 ? 's' : ''} facture{billsForSelectedClosure.length - 5 !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {billsForSelectedClosure.length > 0 && (
              <div className="payment-details-section">
                <h4>💰 RÉPARTITION DES PAIEMENTS (TTC)</h4>
                {Object.entries(calculatePaymentStatsFromBills(billsForSelectedClosure)).map(([method, amount]) => (
                  <div key={method} className="payment-method-row">
                    <span className="method-name">{method.replace(/_/g, " ").toUpperCase()}</span>
                    <span className="method-amount">{amount.toFixed(2)} DT TTC</span>
                  </div>
                ))}
                <div className="payment-total-row">
                  <span className="method-name" style={{fontWeight: 'bold'}}>TOTAL TTC</span>
                  <span className="method-amount" style={{fontWeight: 'bold'}}>
                    {getClosureTotalTTC(selectedClosure).toFixed(2)} DT
                  </span>
                </div>
              </div>
            )}
            
            <div style={{marginTop: '20px', textAlign: 'right'}}>
              <button 
                className="reset-btn"
                onClick={() => setShowDetails(false)}
                style={{marginRight: '10px'}}
              >
                Fermer
              </button>
              <button 
                className="export-btn"
                onClick={() => generateDetailedPDF(selectedClosure)}
              >
                📄 Exporter en PDF (TTC)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalClosure;